const NET30_DAYS = 30

/**
 * @param {Date} date
 */
export function endOfCalendarDay(date) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

/**
 * @param {string | Date | null | undefined} raw
 * @returns {Date | null} Local calendar date (no timezone shift on YYYY-MM-DD values).
 */
export function parseBillingCalendarDate(raw) {
  if (raw == null || raw === '') return null
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate())
  }
  const str = String(raw).trim()
  const datePart = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (datePart) {
    const year = Number(datePart[1])
    const month = Number(datePart[2]) - 1
    const day = Number(datePart[3])
    const d = new Date(year, month, day)
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) {
      return null
    }
    return d
  }
  const parsed = new Date(str)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

/**
 * @param {string | Date | null | undefined} raw
 * @param {{ fallback?: string }} [options]
 */
export function formatBillingCalendarDate(raw, { fallback = '—' } = {}) {
  const d = parseBillingCalendarDate(raw)
  if (!d) return fallback
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * @param {string | Date | null | undefined} raw
 */
export function parseBillingDocumentDateValue(raw) {
  return parseBillingCalendarDate(raw)
}

/**
 * @param {Record<string, unknown> | null | undefined} doc
 * @param {Record<string, unknown> | null | undefined} [entry]
 */
export function resolveQuoteValidUntilDate(doc, entry = null) {
  const source = doc ?? entry?.billingDocument ?? entry
  const raw =
    source?.validUntil ??
    entry?.validUntil ??
    doc?.validUntil ??
    entry?.billingDocument?.validUntil
  const parsed = parseBillingDocumentDateValue(raw)
  if (parsed) return parsed

  const issued = parseBillingDocumentDateValue(
    source?.issuedAt ?? entry?.purchaseDate ?? entry?.issuedAt
  )
  if (!issued) return null
  const fallback = new Date(issued)
  fallback.setDate(fallback.getDate() + NET30_DAYS)
  return fallback
}

/**
 * @param {Record<string, unknown> | null | undefined} doc
 * @param {Record<string, unknown> | null | undefined} [entry]
 */
export function resolveInvoiceDueDate(doc, entry = null) {
  const source = doc ?? entry?.billingDocument ?? entry
  const raw =
    source?.dueAt ?? entry?.dueAt ?? doc?.dueAt ?? entry?.billingDocument?.dueAt
  const parsed = parseBillingDocumentDateValue(raw)
  if (parsed) return parsed

  const issued = parseBillingDocumentDateValue(
    source?.issuedAt ?? entry?.purchaseDate ?? entry?.issuedAt
  )
  if (!issued) return null
  const fallback = new Date(issued)
  fallback.setDate(fallback.getDate() + NET30_DAYS)
  return fallback
}

/**
 * @param {Date | null} date
 * @param {Date} [now]
 */
export function computeDaysUntilDate(date, now = new Date()) {
  if (!date) return null
  return Math.max(0, Math.ceil((endOfCalendarDay(date) - now) / 86400000))
}

/**
 * @param {Record<string, unknown> | null | undefined} docOrEntry
 * @param {Date} [now]
 */
export function isQuoteExpired(docOrEntry, now = new Date()) {
  if (!docOrEntry) return false
  const doc = docOrEntry.billingDocument ?? docOrEntry
  const isQuote =
    doc?.documentType === 'quote' ||
    docOrEntry.documentType === 'quote' ||
    docOrEntry.type === 'Estimate/Quote'
  if (!isQuote) return false
  const validUntil = resolveQuoteValidUntilDate(doc, docOrEntry)
  if (!validUntil) return false
  return now > endOfCalendarDay(validUntil)
}

/**
 * Unpaid invoice past due date (still payable in QuickBooks).
 * PO-received invoices are fulfilled when seats are granted, and a later
 * QuickBooks sync can set status to overdue. That fulfillment is not payment.
 * @param {Record<string, unknown> | null | undefined} docOrEntry
 * @param {Date} [now]
 */
export function isInvoiceOverdue(docOrEntry, now = new Date()) {
  if (!docOrEntry) return false
  const doc = docOrEntry.billingDocument ?? docOrEntry
  const isInvoice =
    doc?.documentType === 'invoice' ||
    docOrEntry.documentType === 'invoice' ||
    docOrEntry.type === 'Invoice'
  if (!isInvoice) return false
  const status = String(docOrEntry.status ?? doc?.status ?? '').toLowerCase()
  if (['paid', 'void', 'voided'].includes(status)) return false
  if (status === 'overdue') return true
  const fulfilled =
    String(docOrEntry.fulfillmentStatus ?? doc?.fulfillmentStatus ?? '').toLowerCase() === 'fulfilled'
  if (fulfilled && status !== 'po_received') return false
  const dueAt = resolveInvoiceDueDate(doc, docOrEntry)
  if (!dueAt) return false
  return now > endOfCalendarDay(dueAt)
}

/**
 * @param {Date} [from]
 */
export function defaultExtensionDate(from = new Date()) {
  const d = new Date(from)
  d.setDate(d.getDate() + NET30_DAYS)
  return d.toISOString().slice(0, 10)
}

/**
 * @param {string | Date | null | undefined} value
 */
export function formatBillingDocumentDateInputValue(value) {
  const d = parseBillingDocumentDateValue(value)
  if (!d) return ''
  const yr = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const dy = String(d.getDate()).padStart(2, '0')
  return `${yr}-${mo}-${dy}`
}
